import React, { useState, useRef, ComponentClass, useEffect } from 'react';
import { EditorState, convertToRaw, ContentState, RawDraftContentState } from 'draft-js';
import Editor, { EditorPlugin } from 'draft-js-plugins-editor';
import createMentionPlugin, { defaultSuggestionsFilter } from 'draft-js-mention-plugin';

import uniqWith from 'lodash/uniqWith';
import Link from 'next/link';
import { useLocation } from 'hooks/useLocation';
import { AuthorFlyout } from '@morressier/ts';
import { scrollElementIntoView } from 'utils/scrollElementIntoVIew';
import { generateEditorStateWithMentions } from './generateEditorStateWithMentions';
import { CommentBoxProps } from './CommentBox';
import * as Styles from './style';

export const getReplyTag = (fullName?: string) => {
  if (fullName) {
    return `@${fullName} `;
  }

  return '';
};

function MentionComponent({ mention }) {
  const origin = useLocation()?.origin;
  const user = {
    id: mention.id,
    picture_url: mention.avatar,
    full_name: mention.name,
    email: mention.email,
    department: mention.department,
    organization: mention.organization,
  } as MorressierUser;

  return (
    <Styles.MentionTag>
      {mention.id ? (
        <AuthorFlyout
          author={user}
          trigger={
            <Link href={`${origin}/profile/${mention.id}`}>
              <a>{getReplyTag(mention.name)}</a>
            </Link>
          }
          xxSmallStyles="left: 40px; bottom: 25px"
          placement="top"
        />
      ) : (
        getReplyTag(mention.name)
      )}
    </Styles.MentionTag>
  );
}

const defaultMentionPlugin: MentionPlugin = createMentionPlugin({
  mentionTrigger: '@',
  entityMutability: 'IMMUTABLE',
  mentionComponent: MentionComponent,
});

interface MentionSuggestionsPublicProps {
  suggestions: Mentions[];
  onSearchChange: ({ value }: { value: unknown }) => void;
  style?: React.CSSProperties;
}

export interface Mentions {
  name: string;
  link?: string;
  avatar?: string;
  id: string;
  department?: string;
  organization?: string;
}

type MentionPlugin = {
  MentionSuggestions: React.ComponentClass<MentionSuggestionsPublicProps, unknown>;
};

export type MentionsEvent = {
  rawString: string;
  stringifiedEditorContent: string;
  editorContent: RawDraftContentState;
  mentions: string[];
};
export type onChange = (args: MentionsEvent) => unknown;

export interface MentionsEditorProps {
  onChange?: onChange;
  mentions?: Mentions[];
  placeholder?: string;
  rows?: number;
  defaultValue?: ContentState;
  style?: React.CSSProperties;
  noBorder?: boolean;
  readOnly?: boolean;
  mentionPlugin?: MentionPlugin | EditorPlugin;
  reset?: boolean;
  handleFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  handleBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  hasMentionBg?: boolean;
  scrollIntoView?: boolean;
  initialMention?: CommentBoxProps['replyTo'];
  externalScroll?: boolean;
}

const MentionsEditor: React.FC<MentionsEditorProps> = props => {
  const {
    defaultValue,
    mentions,
    onChange = () => null,
    placeholder,
    rows,
    style,
    noBorder,
    readOnly,
    mentionPlugin = defaultMentionPlugin,
    reset,
    handleFocus,
    handleBlur,
    scrollIntoView,
    initialMention,
    externalScroll,
  } = props;

  const { MentionSuggestions } = mentionPlugin as MentionPlugin;
  const plugins = [mentionPlugin as EditorPlugin];

  const [editorState, setEditorState] = useState(() => {
    if (defaultValue) {
      return EditorState.createWithContent(defaultValue);
    }

    if (initialMention) {
      return generateEditorStateWithMentions(initialMention);
    }

    return EditorState.createEmpty();
  });

  const editorStateRef = useRef(editorState);
  const editor = useRef<Editor>(null);

  // Focus on editor window
  const focusEditor = React.useCallback(() => {
    const editorCurrent = editor.current;

    if (editorCurrent) {
      editorCurrent.focus();
    }
  }, []);

  useEffect(() => {
    const resetEditor = () => {
      const newEditorState = EditorState.push(
        editorStateRef.current,
        ContentState.createFromText(''),
        'remove-range',
      );

      return setEditorState(newEditorState);
    };

    if (reset) {
      resetEditor();
    }
  }, [reset]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (defaultValue) {
      const defaultContent = JSON.stringify(
        convertToRaw(EditorState.createWithContent(defaultValue).getCurrentContent()),
      );

      const editorContent = JSON.stringify(
        convertToRaw(editorStateRef.current.getCurrentContent()),
      );

      if (defaultContent === editorContent) {
        /**
         * This comment block of code is supposed to move the cursor to the end
         * But it breaks the editor styling so I am leaving it out for now until I find a fix
         */
        timeout = setTimeout(() => {
          focusEditor();
        }, 50);
      } else {
        const newEditorState = EditorState.createWithContent(defaultValue);
        setEditorState(newEditorState);
      }
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [defaultValue, focusEditor]);

  const [suggestions, setSuggestions] = useState<Mentions[] | undefined>(mentions);

  // Check editor text for mentions
  const onSearchChange = ({ value }) => {
    setSuggestions(defaultSuggestionsFilter(value, mentions));
  };

  const handleChange = (eState: EditorState) => {
    setEditorState(eState);
    const content = convertToRaw(eState.getCurrentContent());
    const rawContent = eState.getCurrentContent().getPlainText();

    const mentionsHere = uniqWith(
      Object.values(content.entityMap)
        .filter(entity => entity.type === 'mention')
        .map(entity => entity.data.mention.id),
      (a, b) => a === b,
    );

    if (rawContent.length) {
      return onChange({
        rawString: rawContent.trim(),
        stringifiedEditorContent: JSON.stringify(content),
        editorContent: content,
        mentions: mentionsHere,
      });
    }

    return onChange({
      rawString: '',
      stringifiedEditorContent: JSON.stringify(
        convertToRaw(EditorState.createEmpty().getCurrentContent()),
      ),
      editorContent: convertToRaw(EditorState.createEmpty().getCurrentContent()),
      mentions: mentionsHere,
    });
  };

  const mentionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (externalScroll) {
      // This Focus on editor box area fixes an inconsistency in the focus behaviour
      timeout = setTimeout(() => {
        focusEditor();
      });
    }

    if (scrollIntoView && mentionRef.current) {
      scrollElementIntoView(mentionRef.current, {
        behavior: 'smooth',
        block: 'center',
      }).then(r => {
        if (r.isInView) {
          focusEditor();
        }
      });
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [focusEditor, externalScroll, scrollIntoView]);

  return (
    <Styles.DraftWrapper style={{ padding: 0, margin: 0 }}>
      <Styles.MentionsEditor
        style={{
          minHeight: rows ? `${rows * 2.5}rem` : '1rem',
          maxHeight: readOnly ? 'none' : rows ? `${rows * 2.5}rem` : '1rem', // eslint-disable-line
          overflowY: 'auto',
          ...style,
        }}
        onClick={() => focusEditor()}
        noBorder={noBorder}
        readOnly={readOnly}
        mentionTagHasBg={!readOnly}
      >
        <Editor
          ref={editor}
          editorState={editorState}
          plugins={plugins}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        {suggestions && (
          <Styles.SuggestionsContainer>
            <MentionSuggestions
              onSearchChange={onSearchChange}
              suggestions={suggestions}
              style={{ zIndex: 1 }}
            />
          </Styles.SuggestionsContainer>
        )}
      </Styles.MentionsEditor>
      <div ref={mentionRef} />
    </Styles.DraftWrapper>
  );
};

/**
 * I created this class component to fix a bug I noticed with the createMentionPlugin
 *
 * For some weird reason, if the MentionsEditor is a functional component:
 * the mentions flyout disapears when hovered on
 * I think the issue arises form the createMentionPlugin function
 */
class SimpleMentionsEditor extends React.Component<MentionsEditorProps> {
  mentionPlugin: {
    MentionSuggestions: ComponentClass<MentionSuggestionsPublicProps>;
  };

  constructor(props) {
    super(props);

    this.mentionPlugin = createMentionPlugin({
      mentionTrigger: '@',
      entityMutability: 'IMMUTABLE',
      mentionComponent: MentionComponent,
    });
  }

  render() {
    return <MentionsEditor {...this.props} mentionPlugin={this.mentionPlugin} />;
  }
}

export default SimpleMentionsEditor;
